import { ReactNode, useEffect, useRef, useState } from "react";
import {
  BRACKET_INNER,
  PLACEHOLDER_INNER,
  SCRIPT_TOKEN_REGEX,
  Segment,
  currentSegmentIdx,
  emojify,
} from "../utils";

type Phase = "ready" | "countdown" | "recording";

interface Props {
  /** Lines shown before recording (above `---` in the script). */
  intro: string[];
  /** Recording-time segments (below `---`, or all lines if no separator). */
  segments: Segment[];
  /** Per-segment timing computed from durations + maxSec. */
  timing: { start: number; dur: number }[];
  /** Elapsed seconds in the recording (0 before REC starts). */
  elapsedSec: number;
  /** Current recorder phase — drives intro vs segments rendering. */
  phase: Phase;
}

const LEMONADE = "#FFFC7D";
const KARAOKE_TRACK = "#808080"; // ash — the unfilled portion of the underline
// Target word count per phrase group in "phrase" karaoke mode. Punctuation
// and newlines force a break earlier; an orphan single-word final group is
// merged into the previous one to avoid a lone end-of-segment flash.
const PHRASE_TARGET_WORDS = 3;

/**
 * Segment-based teleprompter.
 *
 * Inline syntax (per line):
 * - `...(text)...` → lemonade-yellow placeholder.
 * - `[text]`       → orange highlight.
 * - Emoticons (`:)`, `;)`, `:p`, `XD`, `<3`, etc.) → unicode emoji.
 *
 * Segment markers (start of line):
 * - `(N)` / `(N.M)` → explicit duration in seconds.
 * - `()`            → auto duration (share remaining time by word count).
 * - `*(N)` / `*()`  → karaoke "phrase" mode: text appears all at once; the
 *                     underline is a continuous bar PER GROUP (~3 words per
 *                     group, punctuation/newlines force an earlier break)
 *                     that fills yellow over an ash-grey track during the
 *                     groups time slot. Past groups stay full yellow; future
 *                     groups have no track until their turn — a classic
 *                     karaoke fill cadence.
 * - `**(N)` / `**()`→ karaoke "instant" mode: full underline appears under
 *                     every word the moment the segment becomes active.
 */
export default function Teleprompter({
  intro,
  segments,
  timing,
  elapsedSec,
  phase,
}: Props) {
  // Hooks first — must run unconditionally to keep order stable across phases.
  const idx = Math.max(0, currentSegmentIdx(elapsedSec, timing));
  const seg = segments[idx] as Segment | undefined;
  const t = timing[idx] ?? { start: 0, dur: 1 };
  const segElapsed = Math.max(0, elapsedSec - t.start);
  const karaokeMode: Segment["karaoke"] | undefined =
    phase === "recording" && t.dur > 0 ? seg?.karaoke : undefined;
  const needsRaf = karaokeMode === "phrase";

  // Capture wall-clock at segment start so we can interpolate sub-second
  // progress between the parent's 1Hz elapsedSec ticks.
  const segOriginWallMs = useRef(performance.now());
  const segOriginSegEl = useRef(0);
  useEffect(() => {
    segOriginWallMs.current = performance.now();
    segOriginSegEl.current = Math.max(0, elapsedSec - t.start);
    // We only want this to fire when the segment changes — capturing the
    // current elapsed AT that moment. Re-running on every elapsedSec tick
    // would reset the wall clock and stutter the underline.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  // RAF loop, active only during a phrase-karaoke segment.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!needsRaf) return;
    let raf = 0;
    const loop = () => {
      setTick((n) => (n + 1) & 0xffff);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [needsRaf, idx]);

  const liveSegEl = needsRaf
    ? segOriginSegEl.current +
      (performance.now() - segOriginWallMs.current) / 1000
    : segElapsed;
  const liveSegFrac =
    t.dur > 0 ? Math.min(1, Math.max(0, liveSegEl / t.dur)) : 0;

  // ── Intro mode (ready / countdown) ─────────────────────────────────
  if (phase !== "recording") {
    const lines = intro.length > 0 ? intro : segments.slice(0, 1).map((s) => s.raw);
    if (lines.length === 0) return null;
    return (
      <div className="flex flex-col items-center justify-center h-full px-6 gap-3">
        {lines.map((line, i) => (
          <div
            key={i}
            className="text-xl md:text-3xl text-ivory/95 font-medium text-center leading-snug max-w-3xl whitespace-pre-line"
          >
            {renderLine(line)}
          </div>
        ))}
      </div>
    );
  }

  // ── Recording mode — active segment based on elapsed time ──────────
  if (segments.length === 0 || !seg) return null;
  const segmentProgress = t.dur > 0 ? Math.min(1, segElapsed / t.dur) : 1;

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 gap-4">
      {segments.length > 1 && (
        <div className="w-full max-w-md flex items-center gap-3">
          <span className="text-ivory/60 text-xs font-mono tabular-nums">
            {idx + 1}/{segments.length}
          </span>
          <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-lemonade transition-[width] duration-100 ease-linear"
              style={{ width: `${segmentProgress * 100}%` }}
            />
          </div>
        </div>
      )}
      <div
        key={idx}
        className="teleprompter-fade text-2xl md:text-4xl text-ivory font-semibold text-center leading-snug max-w-3xl whitespace-pre-line"
      >
        {karaokeMode
          ? renderKaraokeLine(seg.raw, liveSegFrac, karaokeMode)
          : renderLine(seg.raw)}
      </div>
    </div>
  );
}

/** Render one line: emojify, then tokenize for placeholders + brackets. */
function renderLine(rawLine: string): ReactNode {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);
  return parts.map((part, i) => {
    const ph = part.match(PLACEHOLDER_INNER);
    if (ph) {
      return (
        <span key={i} className="text-lemonade font-bold">
          {ph[1]}
        </span>
      );
    }
    const br = part.match(BRACKET_INNER);
    if (br) {
      return (
        <span key={i} className="text-orange-400 font-bold">
          {br[1]}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

type WordUnit = {
  kind: "word";
  text: string;
  color: "plain" | "lemonade" | "orange";
  weight: number;
};
type SpaceUnit = { kind: "space"; text: string };
type KaraokeUnit = WordUnit | SpaceUnit;
type PhraseGroup = { units: KaraokeUnit[]; weight: number };

/** Karaoke rendering. Text flows exactly as it would without any underline
 *  — no inline-block wrappers, no nowrap, so words and line breaks stay
 *  where the user wrote them. Each visible word gets its own absolutely
 *  positioned bar (grey track + yellow fill) tucked below its baseline;
 *  the trailing space of the word is included in the same span so adjacent
 *  bars touch and read as a single line on a visual row.
 *
 *  - `mode === "instant"` → every word receives a full lemonade underline
 *    immediately; signals "say this whole line at once". No animation.
 *  - `mode === "phrase"`  → words are split into groups of roughly
 *    PHRASE_TARGET_WORDS, with punctuation/newlines forcing an earlier
 *    break. Within an active group the bars fill word by word, weighted
 *    by character count. Past groups stay fully filled, future groups
 *    show no bar at all. When a group wraps to two lines, the fill simply
 *    finishes the words on line 1 then continues with the next words on
 *    line 2 — no special wrap handling needed because each word is its
 *    own positioning context. */
function renderKaraokeLine(
  rawLine: string,
  segFrac: number,
  mode: "phrase" | "instant",
): ReactNode {
  const line = emojify(rawLine);
  const parts = line.split(SCRIPT_TOKEN_REGEX).filter(Boolean);

  // Step 1: tokenize the line into word + space units, preserving the
  //         lemonade/orange highlight categories of placeholder & bracket
  //         tokens for downstream colour styling.
  const units: KaraokeUnit[] = [];
  const wordRe = /(\s+|\S+)/g;
  for (const part of parts) {
    const ph = part.match(PLACEHOLDER_INNER);
    const br = part.match(BRACKET_INNER);
    let color: WordUnit["color"] = "plain";
    let inner = part;
    if (ph) {
      color = "lemonade";
      inner = ph[1];
    } else if (br) {
      color = "orange";
      inner = br[1];
    }
    wordRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = wordRe.exec(inner)) !== null) {
      const tok = m[0];
      if (/^\s+$/.test(tok)) {
        units.push({ kind: "space", text: tok });
      } else {
        units.push({
          kind: "word",
          text: tok,
          color,
          weight: Math.max(1, tok.length),
        });
      }
    }
  }

  // Step 2: group units. In "instant" mode the whole segment is a single
  //         group (one continuous bar across everything). In "phrase" mode
  //         we chunk by ~PHRASE_TARGET_WORDS, with punctuation/newlines
  //         forcing an earlier break; a 1-word orphan final group merges
  //         into the previous one to avoid a lone last flash.
  const groups: PhraseGroup[] = [];
  if (mode === "instant") {
    const w = units.reduce(
      (s, u) => s + (u.kind === "word" ? u.weight : 0),
      0,
    );
    groups.push({ units, weight: Math.max(1, w) });
  } else {
    let curUnits: KaraokeUnit[] = [];
    let curWeight = 0;
    let curWords = 0;
    const flushGroup = () => {
      if (curUnits.length === 0) return;
      groups.push({ units: curUnits, weight: Math.max(1, curWeight) });
      curUnits = [];
      curWeight = 0;
      curWords = 0;
    };
    for (const u of units) {
      if (u.kind === "word") {
        curUnits.push(u);
        curWeight += u.weight;
        curWords += 1;
        if (/[,;:.!?…]$/.test(u.text) || curWords >= PHRASE_TARGET_WORDS) {
          flushGroup();
        }
      } else {
        if (u.text.includes("\n") && curUnits.length > 0) flushGroup();
        curUnits.push(u);
      }
    }
    flushGroup();

    if (groups.length >= 2) {
      const last = groups[groups.length - 1];
      const lastWordCount = last.units.filter((u) => u.kind === "word").length;
      if (lastWordCount === 1) {
        const prev = groups[groups.length - 2];
        prev.units = [...prev.units, ...last.units];
        prev.weight += last.weight;
        groups.pop();
      }
    }
  }

  // Step 3: per-group time slots within the segment.
  const totalWeight = groups.reduce((s, g) => s + g.weight, 0) || 1;
  let cum = 0;
  const groupSlots = groups.map((g) => {
    const startFrac = cum / totalWeight;
    cum += g.weight;
    return { startFrac, endFrac: cum / totalWeight };
  });

  const groupState = (
    gi: number,
  ): { progress: number; visible: boolean } => {
    if (mode === "instant") return { progress: 1, visible: true };
    const { startFrac, endFrac } = groupSlots[gi];
    if (segFrac < startFrac) return { progress: 0, visible: false };
    if (segFrac >= endFrac) return { progress: 1, visible: true };
    const slot = endFrac - startFrac;
    if (slot <= 0) return { progress: 1, visible: true };
    return { progress: (segFrac - startFrac) / slot, visible: true };
  };

  // Step 4: flatten each group into a sequence of render items:
  //         - "lex" = a word + any trailing space(s) glued into one span; the
  //           trailing space is INSIDE the span so its bar extends across the
  //           gap to the next word, producing a visually continuous line on
  //           one row. The word also carries the per-word progress.
  //         - "newline" = a `\n`-bearing space, rendered as a plain span so
  //           the parent's `whitespace-pre-line` turns it into a line break.
  //         - "leading" = a non-newline space that appears BEFORE the first
  //           word of a group (the gap between two groups). Rendered as a
  //           plain span with no bar so the visual gap stays clean.
  type Lex = {
    kind: "lex";
    text: string;
    color: WordUnit["color"];
    weight: number; // characters of word + trailing space, drives the bar split
  };
  type Plain = { kind: "plain"; text: string };
  type RenderItem = (Lex | Plain) & { groupIdx: number };

  const items: RenderItem[] = [];
  groups.forEach((g, gi) => {
    let pending = "";
    let lastLexIdx = -1;
    for (const u of g.units) {
      if (u.kind === "word") {
        if (pending) {
          items.push({ kind: "plain", text: pending, groupIdx: gi });
          pending = "";
        }
        items.push({
          kind: "lex",
          text: u.text,
          color: u.color,
          weight: u.weight,
          groupIdx: gi,
        });
        lastLexIdx = items.length - 1;
      } else {
        if (u.text.includes("\n")) {
          if (pending) {
            items.push({ kind: "plain", text: pending, groupIdx: gi });
            pending = "";
          }
          items.push({ kind: "plain", text: u.text, groupIdx: gi });
          lastLexIdx = -1;
        } else if (lastLexIdx >= 0) {
          // Attach as trailing space to the last lex so its bar extends.
          const last = items[lastLexIdx] as Lex & { groupIdx: number };
          last.text += u.text;
          last.weight += u.text.length;
        } else {
          // Leading space before the first word of the group.
          pending += u.text;
        }
      }
    }
    if (pending) items.push({ kind: "plain", text: pending, groupIdx: gi });
  });

  // Step 5: compute per-lex progress thresholds WITHIN each group, weighted
  //         by the lex's char count (word + trailing) so longer chunks get
  //         a proportionally larger share of the group time slot.
  const perGroupTotals: number[] = groups.map(() => 0);
  for (const it of items) {
    if (it.kind === "lex") perGroupTotals[it.groupIdx] += it.weight;
  }
  for (let i = 0; i < perGroupTotals.length; i++) {
    if (perGroupTotals[i] === 0) perGroupTotals[i] = 1;
  }
  const perGroupCum: number[] = groups.map(() => 0);

  return items.map((it, idx) => {
    if (it.kind === "plain") {
      return <span key={idx}>{it.text}</span>;
    }
    const { progress: gp, visible } = groupState(it.groupIdx);
    const total = perGroupTotals[it.groupIdx];
    const startFrac = perGroupCum[it.groupIdx] / total;
    perGroupCum[it.groupIdx] += it.weight;
    const endFrac = perGroupCum[it.groupIdx] / total;
    const range = Math.max(0.0001, endFrac - startFrac);
    const localProgress = Math.min(
      1,
      Math.max(0, (gp - startFrac) / range),
    );

    const colorClass =
      it.color === "lemonade"
        ? "text-lemonade font-bold"
        : it.color === "orange"
        ? "text-orange-400 font-bold"
        : "";

    return (
      <span
        key={idx}
        className={colorClass}
        style={{ position: "relative" }}
      >
        {it.text}
        {visible && (
          <>
            <span
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "-4px",
                height: "3px",
                backgroundColor: KARAOKE_TRACK,
                pointerEvents: "none",
              }}
            />
            <span
              aria-hidden
              style={{
                position: "absolute",
                left: 0,
                bottom: "-4px",
                height: "3px",
                width: `${localProgress * 100}%`,
                backgroundColor: LEMONADE,
                pointerEvents: "none",
              }}
            />
          </>
        )}
      </span>
    );
  });
}
